import React, { Fragment, useEffect, useMemo, useRef } from 'react';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { resolveTimeFilterField } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import { isExternalLayerInCurrentScope } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isExternalLayerInCurrentScope';
import type { SelectedLayerForAI } from './utils/prefetchLayerFeaturesForAI';
import type { AiSpatialFilter } from './utils/aiMapSpatialFilter';
import {
  buildAiLayerCatalog,
  groupAiLayersByCategory,
  sameExternalLayerForAi,
  type AiLayerCatalogExternal,
} from './utils/buildAiLayerCatalog';
import LayersDatetimeFilter from './LayersDatetimeFilter';
import {
  applyHideLayersOnMap,
  applyShowLayersOnMap,
  layersToHideOnMap,
  layersToShowOnMap,
  selectedLayersSupportDatetimeFilter,
  stampSpatialFilter,
} from './utils/addSelectedLayerForAI';
import { stripTimeFields } from './utils/useSelectedLayersTimeRange';

type ChatLayerSelectorProps = {
  selectedLayers: SelectedLayerForAI[];
  onChange: (layers: SelectedLayerForAI[]) => void;
  filterByDatetime?: boolean;
  onFilterByDatetimeChange?: (enabled: boolean) => void;
  /** When true, newly checked layers are turned on in the ArcGIS map. */
  syncToMap?: boolean;
  /** Keep map sync without showing the picker. */
  syncOnly?: boolean;
  defaultSpatialFilter?: AiSpatialFilter;
  defaultSpatialFilterLabel?: string;
  /** When set, the dropdown only offers layers this returns true for. */
  isLayerAllowed?: (layer: SelectedLayerForAI) => boolean;
};

function layerKey(layer: Pick<SelectedLayerForAI, 'kind' | 'id'>) {
  return `${layer.kind}:${layer.id}`;
}

function sameLayerIdentity(
  a: Pick<SelectedLayerForAI, 'kind' | 'id'>,
  b: Pick<SelectedLayerForAI, 'kind' | 'id'>
) {
  return a.kind === b.kind && a.id === b.id;
}

const noop = () => {};
const noopFalse = () => false;

/**
 * Multi-select of ArcGIS (external) and Pratus-hosted (internal) layers for AI
 * data sources. Catalog lives in a searchable dropdown; selections are pills.
 */
const ChatLayerSelector: React.FC<ChatLayerSelectorProps> = ({
  selectedLayers,
  onChange,
  filterByDatetime = false,
  onFilterByDatetimeChange,
  syncToMap = false,
  syncOnly = false,
  defaultSpatialFilter,
  defaultSpatialFilterLabel,
  isLayerAllowed,
}) => {
  const catalogAnchor = useComboboxAnchor();
  const groupGuid = useGroupGuid();
  const { data: incident } = useIncident();
  const incidentId = incident?.id;
  const mapCtx = useOptionalUscgMapContext();
  const externalLayerTimeFilterById = mapCtx?.externalLayerTimeFilterById ?? {};
  const isExternalLayerVisible = mapCtx?.isExternalLayerVisible ?? noopFalse;
  const toggleExternalLayerVisibility =
    mapCtx?.toggleExternalLayerVisibility ?? noop;
  const isInternalLayerVisible = mapCtx?.isInternalLayerVisible ?? noopFalse;
  const toggleInternalLayerVisibility =
    mapCtx?.toggleInternalLayerVisibility ?? noop;
  const setIsMapVisible = mapCtx?.setIsMapVisible ?? noop;
  const setExternalLayerTimeFilter = mapCtx?.setExternalLayerTimeFilter ?? noop;
  const setLayerDefinitionExpressionTimePart =
    mapCtx?.setLayerDefinitionExpressionTimePart ?? noop;
  const filterByDatetimeRef = useRef(filterByDatetime);
  const selectedLayersRef = useRef(selectedLayers);
  useEffect(() => {
    filterByDatetimeRef.current = filterByDatetime;
    selectedLayersRef.current = selectedLayers;
  }, [filterByDatetime, selectedLayers]);
  const listEnabled = Boolean(groupGuid);

  const { data: apiKeyExternal, isLoading: loadingApiKey } =
    useListExternalLayersQuery(
      {
        filter: {
          authType: 'apiKey',
          groupGuid: groupGuid ?? '',
          incidentId: incidentId || undefined,
        },
        perPage: 200,
      },
      { enabled: listEnabled }
    );

  const { data: publicExternal, isLoading: loadingPublic } =
    useListExternalLayersQuery(
      {
        filter: {
          authType: 'public',
          groupGuid: groupGuid ?? '',
          incidentId: incidentId || undefined,
        },
        perPage: 200,
      },
      { enabled: listEnabled }
    );

  const { data: orgExternal, isLoading: loadingOrg } =
    useListExternalLayersQuery(
      {
        filter: {
          authType: 'userAuth',
          groupGuid: groupGuid ?? '',
          incidentId: incidentId || undefined,
        },
        perPage: 200,
      },
      { enabled: listEnabled }
    );

  const { data: internalLayers, isLoading: loadingInternal } =
    useListInternalLayersQuery({
      filter: {
        groupGuid: groupGuid ?? '',
        ...(incidentId ? { incidentId } : {}),
      },
      perPage: 200,
    });

  const catalog = useMemo(() => {
    const apiLayers = [
      ...(apiKeyExternal?.data ?? []),
      ...(publicExternal?.data ?? []),
      ...(orgExternal?.data ?? []),
    ].filter((layer) => isExternalLayerInCurrentScope(layer, incidentId));

    const externalItems = buildAiLayerCatalog({ apiLayers });

    const internalItems: SelectedLayerForAI[] = (
      internalLayers?.data ?? []
    ).map((layer) => ({
      kind: 'internal',
      id: String(layer.id),
      name: layer.name || 'Untitled internal layer',
      versionId: layer.draftVersionId
        ? String(layer.draftVersionId)
        : undefined,
    }));

    return { externalItems, internalItems };
  }, [
    apiKeyExternal?.data,
    publicExternal?.data,
    orgExternal?.data,
    internalLayers?.data,
    incidentId,
  ]);

  const pickerCatalog = useMemo(() => {
    if (!isLayerAllowed) return catalog;
    return {
      externalItems: catalog.externalItems.filter(isLayerAllowed),
      internalItems: catalog.internalItems.filter(isLayerAllowed),
    };
  }, [catalog, isLayerAllowed]);

  const layerByKey = useMemo(() => {
    const map = new Map<string, SelectedLayerForAI>();
    for (const layer of [
      ...pickerCatalog.internalItems,
      ...pickerCatalog.externalItems,
      ...selectedLayers,
    ]) {
      const key = layerKey(layer);
      if (!map.has(key)) map.set(key, layer);
    }
    return map;
  }, [
    pickerCatalog.externalItems,
    pickerCatalog.internalItems,
    selectedLayers,
  ]);

  const catalogGroups = useMemo(() => {
    const groups: { label: string; items: string[] }[] = [];
    if (pickerCatalog.internalItems.length > 0) {
      groups.push({
        label: 'Pratus-hosted (internal)',
        items: pickerCatalog.internalItems.map(layerKey),
      });
    }
    for (const group of groupAiLayersByCategory(pickerCatalog.externalItems)) {
      groups.push({
        label: group.label,
        items: group.items.map(layerKey),
      });
    }
    const otherExternal = pickerCatalog.externalItems.filter(
      (layer) => !layer.category
    );
    if (otherExternal.length > 0) {
      groups.push({
        label: 'ArcGIS-hosted (external)',
        items: otherExternal.map(layerKey),
      });
    }
    return groups;
  }, [pickerCatalog.externalItems, pickerCatalog.internalItems]);

  const withCurrentTimeFilter = (
    layer: SelectedLayerForAI
  ): SelectedLayerForAI => {
    if (!filterByDatetimeRef.current) return layer;
    const filter = externalLayerTimeFilterById[layer.id];
    if (!filter) return layer;
    const timeFilterField = resolveTimeFilterField(layer);
    return {
      ...layer,
      timeExtent: {
        startIso: filter.startIso,
        endIso: filter.endIso,
      },
      timeFilterLabel: filter.label,
      ...(timeFilterField ? { timeFilterField } : {}),
    };
  };

  const withDefaultScope = (layer: SelectedLayerForAI): SelectedLayerForAI => {
    const next = withCurrentTimeFilter(layer);
    if (!defaultSpatialFilter) return next;
    return stampSpatialFilter(next, {
      spatialFilter: defaultSpatialFilter,
      spatialFilterLabel: defaultSpatialFilterLabel,
    });
  };

  const mapVisibilityApi = {
    isExternalLayerVisible,
    toggleExternalLayerVisibility,
    isInternalLayerVisible,
    toggleInternalLayerVisibility,
  };

  const showOnMap = (items: ReturnType<typeof layersToShowOnMap>) => {
    if (items.length === 0) return;
    const turnedOn = applyShowLayersOnMap(items, mapVisibilityApi);
    if (turnedOn || items.length > 0) {
      setIsMapVisible(true);
    }
  };

  const hideOnMap = (items: ReturnType<typeof layersToHideOnMap>) => {
    if (items.length === 0) return;
    applyHideLayersOnMap(items, mapVisibilityApi);
  };

  const showDatetimeFilter =
    Boolean(onFilterByDatetimeChange) &&
    selectedLayersSupportDatetimeFilter(selectedLayers);

  useEffect(() => {
    if (!onFilterByDatetimeChange) return;
    if (selectedLayersSupportDatetimeFilter(selectedLayers)) return;
    const hadStamp = selectedLayers.some(
      (layer) => layer.timeExtent || layer.timeFilterLabel
    );
    if (hadStamp) {
      onChange(selectedLayers.map(stripTimeFields));
    }
    if (filterByDatetime) {
      onFilterByDatetimeChange(false);
    }
    if (hadStamp || filterByDatetime) {
      for (const layer of selectedLayers) {
        if (layer.kind !== 'external') continue;
        setExternalLayerTimeFilter(layer.id, null);
        setLayerDefinitionExpressionTimePart(layer.id, null);
      }
    }
  }, [
    filterByDatetime,
    onChange,
    onFilterByDatetimeChange,
    selectedLayers,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  ]);

  const hydratedMapRef = useRef(false);
  useEffect(() => {
    if (!syncToMap || hydratedMapRef.current) return;
    const items = layersToShowOnMap([], selectedLayers, catalog.externalItems);
    if (items.length === 0) return;
    const turnedOn = applyShowLayersOnMap(items, {
      isExternalLayerVisible,
      toggleExternalLayerVisibility,
      isInternalLayerVisible,
      toggleInternalLayerVisibility,
    });
    if (turnedOn || items.length > 0) {
      setIsMapVisible(true);
    }
    hydratedMapRef.current = true;
  }, [
    syncToMap,
    selectedLayers,
    catalog.externalItems,
    isExternalLayerVisible,
    toggleExternalLayerVisibility,
    isInternalLayerVisible,
    toggleInternalLayerVisibility,
    setIsMapVisible,
  ]);

  const applySelectedKeys = (keys: string[]) => {
    const current = selectedLayersRef.current;
    const next: SelectedLayerForAI[] = [];
    for (const key of keys) {
      const existing = current.find((layer) => layerKey(layer) === key);
      if (existing) {
        next.push(existing);
        continue;
      }
      const catalogLayer = layerByKey.get(key);
      if (!catalogLayer) continue;
      const nextLayer: AiLayerCatalogExternal = { ...catalogLayer };
      delete nextLayer.category;
      if (
        next.some(
          (layer) =>
            sameExternalLayerForAi(layer, nextLayer) ||
            sameLayerIdentity(layer, nextLayer)
        )
      ) {
        continue;
      }
      next.push(withDefaultScope(nextLayer));
    }
    onChange(next);
    const showItems = layersToShowOnMap(current, next, catalog.externalItems);
    if (syncToMap) {
      hideOnMap(layersToHideOnMap(current, next, catalog.externalItems));
      showOnMap(showItems);
    }
  };

  const selectedKeys = selectedLayers.map(layerKey);
  const isLoading =
    loadingApiKey || loadingPublic || loadingOrg || loadingInternal;

  if (syncOnly) return null;

  if (isLoading) {
    return <p className="txt--sml text-muted mb-0">Loading layers…</p>;
  }

  if (
    pickerCatalog.externalItems.length === 0 &&
    pickerCatalog.internalItems.length === 0 &&
    selectedLayers.length === 0
  ) {
    return (
      <p className="txt--sml text-muted mb-0">
        No layers available. Add ArcGIS or Pratus layers in the Layers tab.
      </p>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {onFilterByDatetimeChange && showDatetimeFilter ? (
        <LayersDatetimeFilter
          selectedLayers={selectedLayers}
          filterByDatetime={filterByDatetime}
          onFilterByDatetimeChange={onFilterByDatetimeChange}
          onSelectedLayersChange={onChange}
        />
      ) : null}

      <Combobox
        multiple
        items={catalogGroups}
        value={selectedKeys}
        onValueChange={(next) => {
          applySelectedKeys((next as string[]) ?? []);
        }}
        itemToStringValue={(item) =>
          typeof item === 'string' ? (layerByKey.get(item)?.name ?? item) : ''
        }
      >
        <div ref={catalogAnchor} className="tw:w-full">
          <ComboboxChips className="dark tw:w-full">
            <ComboboxValue>
              {selectedKeys.map((key) => (
                <ComboboxChip key={key}>
                  {layerByKey.get(key)?.name ?? key}
                </ComboboxChip>
              ))}
            </ComboboxValue>
            <ComboboxTrigger className="tw:ml-auto tw:shrink-0" />
          </ComboboxChips>
        </div>
        <ComboboxContent
          className="dark"
          anchor={catalogAnchor}
          side="bottom"
          align="start"
        >
          <ComboboxInput placeholder="Search layers..." showTrigger={false} />
          <ComboboxEmpty>No matching layers.</ComboboxEmpty>
          <ComboboxList>
            {(group: { label: string; items: string[] }, index: number) => (
              <Fragment key={group.label}>
                {index > 0 ? <ComboboxSeparator /> : null}
                <ComboboxGroup items={group.items}>
                  <ComboboxLabel>{group.label}</ComboboxLabel>
                  <ComboboxCollection>
                    {(item: string) => (
                      <ComboboxItem key={item} value={item}>
                        {layerByKey.get(item)?.name ?? item}
                      </ComboboxItem>
                    )}
                  </ComboboxCollection>
                </ComboboxGroup>
              </Fragment>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};

export default ChatLayerSelector;
